import {
    type StripeSubscriptionSummary,
    SubscriptionProductId,
} from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import { Platform } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckCircleIcon,
    H5Regular,
    Icon,
    ScreenContainer,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { formatReadableMonthDay } from '../../../common/utils/dateUtils';
import useGetSubscription from '../../../settings/hooks/useGetSubscription';
import { getSubscriptionBenefits } from '../../../settings/types/SubscriptionSummary';
import { useUser } from '../../../user/providers/UserProvider';

// Throwaway POC screen: mirrors the Figma "Upgrade to Premium" comparison
// screen's content/functionality, not its exact visual design.
export default function UpgradeComparisonPage() {
    if (
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'local' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'sandbox' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'staging'
    ) {
        return <Redirect href="/" />;
    }

    const { user, loading: isLoadingUser } = useUser();
    const { data: subscription, isLoading: isLoadingSubscription } =
        useGetSubscription(
            { userId: user?.id ?? '' },
            { enabled: user?.id != null },
        );

    return (
        <ScreenContainer
            title="Fruitful Premium"
            hasBackButton
            onFallbackRoute="/dev"
        >
            {isLoadingUser || isLoadingSubscription ? (
                <Spinner />
            ) : (
                <ComparisonContent subscription={subscription} />
            )}
        </ScreenContainer>
    );
}

const ComparisonContent = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary | null | undefined;
}) => {
    const product = subscription?.product;

    if (product == null) {
        return (
            <BodyMediumRegular>
                No subscription found for this test user.
            </BodyMediumRegular>
        );
    }

    // Same trick Subscription.tsx uses: Premium shares the trial's banking
    // rates, so overriding id/displayName is enough to derive its benefits.
    const premiumBenefits = getSubscriptionBenefits({
        ...product,
        id: SubscriptionProductId.PREMIUM,
        displayName: 'Premium',
    });

    if (product.id === SubscriptionProductId.PREMIUM) {
        return (
            <BodyMediumRegular>
                This membership is already on Premium.
            </BodyMediumRegular>
        );
    }

    const nextProduct = subscription?.nextPhase?.product;
    if (nextProduct?.id === SubscriptionProductId.PREMIUM) {
        const switchDate = subscription?.nextPhase?.startDate;
        return (
            <BodyMediumRegular>
                This membership is already scheduled to switch to Premium
                {switchDate != null
                    ? ` on ${formatReadableMonthDay(switchDate)}`
                    : ''}
                .
            </BodyMediumRegular>
        );
    }

    // TODO: Update to handle different changes (premium trail > premium, free > premium, legacy products > premium)

    return (
        <YStack gap={24} width="100%">
            <YStack gap={4}>
                <H5Regular>Upgrade to Premium</H5Regular>
                <BodyMediumRegular color="$gray1000">
                    Get one-on-one guidance and all Premium benefits by
                    upgrading your membership below.
                </BodyMediumRegular>
            </YStack>
            <YStack
                gap={12}
                padding={20}
                borderRadius={24}
                backgroundColor="$peachLight"
            >
                {premiumBenefits.map((benefit, index) => (
                    <XStack key={index} alignItems="flex-start" gap={8}>
                        <Icon
                            icon={CheckCircleIcon}
                            size={20}
                            color="$peachDarkest"
                        />
                        <BodyMediumRegular flex={1}>
                            {benefit}
                        </BodyMediumRegular>
                    </XStack>
                ))}
            </YStack>
            <Button
                text="Upgrade to Premium"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                onPress={() => {
                    router.push('/dev/stripe/upgrade-checkout');
                }}
            />
            {Platform.OS !== 'web' && (
                <Button
                    text="Try PaymentSheet variation"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    onPress={() => {
                        router.push(
                            '/dev/stripe/upgrade-checkout-payment-sheet',
                        );
                    }}
                />
            )}
        </YStack>
    );
};
